import { Navigate, Outlet, Route } from 'react-router-dom';

import { BuyerCompleteAccountPage } from '@/pages/buyer/pages/complete-account/complete-account.page';
import { RestrictedAccessPage } from '@/pages/restricted-access/RestrictedAccess.page';
import { AccountEditEmailPage } from '@/pages/shared/account/pages/edit-email/EditEmail.page';
import { AccountEditNamePage } from '@/pages/shared/account/pages/edit-name/EditName.page';
import { AccountEditPasswordPage } from '@/pages/shared/account/pages/edit-password/EditPassword.page';
import { AccountPage } from '@/pages/shared/account/pages/index/Account.page';
import { AccountLegalPage } from '@/pages/shared/account/pages/legal/AccountLegal.page';
import { AccountTeamPage } from '@/pages/shared/account/pages/team/AccountTeam.page';
import { AccountTeamEditMemberPage } from '@/pages/shared/account/pages/team/pages/edit-member/AccountTeamEditMember.page';
import { AccountTeamInvitePage } from '@/pages/shared/account/pages/team/pages/invite/AccountTeamInvite.page';
import { ComplianceFrameworksPage } from '@/pages/shared/compliance-frameworks/ComplianceFrameworks.page';
import { PlotsPage } from '@/pages/shared/plots/Plots.page';
import { NDVIDetailPage } from '@/pages/shared/projects/pages/project/pages/conservation/restoration/pages/ndvi/NDVIDetail.page';
import { InteractiveMap } from '@/pages/shared/projects/pages/project/pages/conservation/shared/pages/interactive-map/interactive-map.page';
import { BiodiversityDetailPage } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/biodiversity/BiodiversityDetail.page';
import { CarbonDetailPage } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/carbon/CarbonDetail.page';
import { DeforestationDetailPage } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/deforestation/DeforestationDetail.page';
import { ForestStructureDetailPage } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/forestStructure/ForestStructureDetail.page';
import { PlotPage } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/Plot.page';
import { ProjectPage } from '@/pages/shared/projects/pages/project/Project.page';
import { ProjectsPage } from '@/pages/shared/projects/Projects.page';
import { paths } from '@/routing';
import {
  EcologicalIntegrityPages,
  HabitatIntactnessPages,
  IndicatorSpeciesPages,
} from '@/routing/components/ConservationPages';
import { WaterDetailPages } from '@/routing/components/ProjectTypePages';
import { RequireAccessiblePlot } from '@/routing/components/protections/require-accessible-plot/RequireAccessiblePlot';
import { RequireAccessibleProject } from '@/routing/components/protections/require-accessible-project/RequireAccessibleProject';
import { RequireAdminRole } from '@/routing/components/protections/require-admin-role/RequireAdminRole';
import { RequireSupportPlot } from '@/routing/components/protections/require-support-plot/RequireSupportPlot';
import { SkipLoggedInUser } from '@/routing/components/protections/skip-logged-in-user/SkipLoggedInUser';

import { BuyerAccountPageWrapper } from '../pages/account/account.page-wrapper';
import { BuyerInvestmentsPage } from '../pages/investments/Investments.page';
import { BuyerPortfolioPage } from '../pages/portfolio/Portfolio.page';
import { BuyerComplianceFrameworksPageWrapper } from '../pages/projects/pages/compliance-frameworks/ComplianceFrameworks.page-wrapper';
import { BuyerSignedInRoute } from './components/protections/buyer-signed-in-route/BuyerSignedInRoute';

export const BuyerPageRoutes = (
  <Route path='buyer'>
    <Route element={<BuyerSignedInRoute />}>
      <Route path={paths.buyer.account} element={<BuyerAccountPageWrapper />}>
        <Route index element={<AccountPage />} />
        <Route path={paths.buyer.accountEditEmail} element={<AccountEditEmailPage />} />
        <Route path={paths.buyer.accountEditName} element={<AccountEditNamePage />} />
        <Route path={paths.buyer.accountEditPassword} element={<AccountEditPasswordPage />} />
        <Route path={paths.buyer.accountLegal} element={<AccountLegalPage />} />
        <Route path={paths.buyer.accountTeam}>
          <Route index element={<AccountTeamPage />} />
          <Route element={<RequireAdminRole />}>
            <Route path={paths.buyer.accountTeamEditMember} element={<AccountTeamEditMemberPage />} />
            <Route path={paths.buyer.accountTeamInvite} element={<AccountTeamInvitePage />} />
          </Route>
        </Route>
      </Route>

      <Route path={paths.buyer.portfolio} element={<BuyerPortfolioPage />} />
      <Route index element={<Navigate to={paths.buyer.projects} />} />
      <Route path={paths.buyer.investments} element={<BuyerInvestmentsPage />} />
      <Route element={<RequireSupportPlot />}>
        <Route path={paths.buyer.plots} element={<PlotsPage />} />
      </Route>
      <Route path={paths.buyer.projects}>
        <Route index element={<ProjectsPage />} />
        <Route path={paths.buyer.projectsComplianceFrameworks} element={<BuyerComplianceFrameworksPageWrapper />}>
          <Route index element={<ComplianceFrameworksPage />} />
        </Route>
        <Route element={<RequireAccessibleProject />}>
          <Route path={paths.buyer.ecologicalIntegrity} element={<EcologicalIntegrityPages />} />
          <Route path={paths.buyer.habitatIntactness} element={<HabitatIntactnessPages />} />
          <Route path={paths.buyer.indicatorSpecies} element={<IndicatorSpeciesPages />} />
          <Route path={paths.buyer.ndvi} element={<NDVIDetailPage />} />
          <Route path={`${paths.buyer.projectDetails}/*`} element={<ProjectPage />} />
          <Route path={paths.buyer.interactiveMap} element={<InteractiveMap />} />
          <Route path={paths.buyer.projectDetailsBiodiversity} element={<BiodiversityDetailPage />} />
          <Route path={paths.buyer.projectDetailsCarbon} element={<CarbonDetailPage />} />
          <Route path={paths.buyer.projectDetailsWater} element={<WaterDetailPages />} />
          <Route path={paths.buyer.projectDetailsDeforestation} element={<DeforestationDetailPage />} />
          <Route path={paths.buyer.projectDetailsForest} element={<ForestStructureDetailPage />} />
          <Route element={<RequireAccessiblePlot />}>
            <Route path={paths.buyer.plot} element={<PlotPage />} />
          </Route>
        </Route>
      </Route>

      <Route path={paths.buyer.restrictedAccess} element={<RestrictedAccessPage />} />
    </Route>

    <Route
      element={
        <SkipLoggedInUser>
          <Outlet />
        </SkipLoggedInUser>
      }
    >
      <Route path={paths.buyer.completeAccount} element={<BuyerCompleteAccountPage />} />
    </Route>
  </Route>
);
